import api from "../axios";

export type ClaimType = "damage" | "unpaid-rent" | "both";

const CLAIM_TYPE_LABELS: Record<ClaimType, string> = {
  damage: "Property Damage",
  "unpaid-rent": "Unpaid Rent",
  both: "Property Damage & Unpaid Rent",
};

export function formatClaimType(type: string | undefined | null): string {
  if (!type) return "";
  return CLAIM_TYPE_LABELS[type as ClaimType] ?? type;
}

export type ClaimStatus =
  | "incomplete"
  | "submitted"
  | "processing"
  | "disputed"
  | "additional-info-required"
  | "closed-settled"
  | "closed-unsupported"
  | "closed-legal-dispute"
  | "closed-bankrupcy"
  | "withdrawn"
  | "denied"
  | "approved"
  | "recovery"
  | "collections"
  | "completed"
  | "paid"
  | "uncollectable"
  | "closed";

export type ClaimInstallmentPlanType =
  | "pay-in-full"
  | "3-month"
  | "6-month"
  | "12-month"
  | "18-month";

export type ClaimInstallmentStatus =
  | "scheduled"
  | "processing"
  | "succeeded"
  | "failed"
  | "cancelled";

export type ClaimInstallmentPlanStatus =
  | "active"
  | "completed"
  | "cancelled"
  | "failed";

export interface ClaimInstallmentSummary {
  id: number;
  uuid: string;
  sequence: number;
  /** Dollars. */
  amount: number;
  dueDate: string;
  status: ClaimInstallmentStatus;
  attemptedAt: string | null;
}

export interface ClaimInstallmentPlanSummary {
  uuid: string;
  planType: ClaimInstallmentPlanType;
  status: ClaimInstallmentPlanStatus;
  /** The saved PaymentMethod the plan currently charges. */
  paymentMethodId: number;
  /** Display snapshot of the charged method (resolved even if removed). */
  paymentMethod: {
    id: number;
    type: string;
    last4: string | null;
    brand: string | null;
    bankName: string | null;
  } | null;
  installments: number;
  /** Dollars. */
  totalAmount: number;
  /** Dollars. */
  monthlyAmount: number;
  /** Dollars. */
  discountAmount: number;
  /** Dollars. */
  monthlyFee: number;
  /** Dollars. */
  paidAmount: number;
  /** Dollars. */
  remainingAmount: number;
  nextDueDate: string | null;
  installmentsList: ClaimInstallmentSummary[];
}

export interface ClaimSummary {
  id: number;
  uuid: string;
  createdAt: string;
  updatedAt: string;
  status: ClaimStatus;
  type: "damage" | "unpaid-rent" | "both";
  requestAmount: number;
  totalRequestAmount?: number;
  description?: string;
  moveOutDate?: string;
  installmentPlan?: ClaimInstallmentPlanSummary | null;
  policy: {
    uuid: string;
    name: string;
    status: "pending" | "covered" | "revoked" | "expired";
    coverageAmount: number | null;
    startsOn: string | null;
    endsOn: string | null;
    unit: {
      name: string;
      property: {
        name: string;
        address: string;
      } | null;
    } | null;
  } | null;
}

export function getMyClaims(policyUuid?: string) {
  return api.get<ClaimSummary[]>("/claims", {
    params: policyUuid ? { policyUuid } : undefined,
  });
}

export function getClaim(uuid: string) {
  return api.get<ClaimSummary>(`/claims/${uuid}`);
}

export type PayClaimStatus =
  | "pending"
  | "processing"
  | "succeeded"
  | "failed"
  | "cancelled";

export interface PayClaimResult {
  transactionUuid: string;
  status: PayClaimStatus;
  amount: number;
  claimStatus: string;
  paymentIntentId: string;
  planUuid: string;
  planType: ClaimInstallmentPlanType;
  installments: number;
  totalAmount: number;
  error?: string;
}

export function payClaim(
  uuid: string,
  paymentMethodId: number,
  planType: ClaimInstallmentPlanType,
) {
  return api.post<PayClaimResult>(`/claims/${uuid}/pay`, {
    paymentMethodId,
    planType,
  });
}

export function updatePlanPaymentMethod(uuid: string, paymentMethodId: number) {
  return api.patch<{ planUuid: string; paymentMethodId: number }>(
    `/claims/${uuid}/plan/payment-method`,
    { paymentMethodId },
  );
}

// ----- Plan calculator (display only — server recomputes authoritatively) ----

export interface PlanPreview {
  planType: ClaimInstallmentPlanType;
  installments: number;
  /** Cents. */
  monthlyAmount: number;
  /** Cents. */
  totalAmount: number;
  /** Cents. */
  discountAmount: number;
  /** Cents. */
  monthlyFee: number;
}

export function isPlanEligible(
  planType: ClaimInstallmentPlanType,
  claimRequestAmount: number,
): boolean {
  if (planType === "12-month") return claimRequestAmount > 1000;
  if (planType === "18-month") return claimRequestAmount > 2500;
  return true;
}

/**
 * Mirrors the server `installment-plan.util` math for UI preview. Inputs and
 * outputs are dollar amounts (not cents) so the page can render them directly.
 */
export function previewPlan(
  planType: ClaimInstallmentPlanType,
  claimRequestAmount: number,
): PlanPreview {
  const amount = claimRequestAmount;
  switch (planType) {
    case "pay-in-full": {
      const discount = Number((amount * 0.1).toFixed(2));
      const total = Number((amount - discount).toFixed(2));
      return {
        planType,
        installments: 1,
        monthlyAmount: total,
        totalAmount: total,
        discountAmount: discount,
        monthlyFee: 0,
      };
    }
    case "3-month":
      return equalSchedule(planType, amount, 3, 0);
    case "6-month":
      return equalSchedule(planType, amount, 6, 0);
    case "12-month":
      return equalSchedule(planType, amount, 12, 20);
    case "18-month":
      return equalSchedule(planType, amount, 18, 30);
  }
}

function equalSchedule(
  planType: ClaimInstallmentPlanType,
  amount: number,
  installments: number,
  monthlyFee: number,
): PlanPreview {
  const monthlyAmount = Number((amount / installments + monthlyFee).toFixed(2));
  const totalAmount = Number((amount + monthlyFee * installments).toFixed(2));
  return {
    planType,
    installments,
    monthlyAmount,
    totalAmount,
    discountAmount: 0,
    monthlyFee,
  };
}

export interface ClaimDocumentSummary {
  id: number;
  uuid: string;
  createdAt: string;
  type: string;
  documentTypeTitle: string | null;
  fileName: string;
}

export function getClaimDocuments(uuid: string) {
  return api.get<ClaimDocumentSummary[]>(`/claims/${uuid}/documents`);
}

export function getClaimDocumentDownloadUrl(
  claimUuid: string,
  documentUuid: string,
) {
  return api.get<{ downloadUrl: string }>(
    `/claims/${claimUuid}/documents/${documentUuid}/download`,
  );
}
